import { useCallback, useEffect, useState } from 'react'
import { 
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  deleteUser,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  updateProfile,
  verifyBeforeUpdateEmail,
} from 'firebase/auth'
import { doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { auth, db } from '../config/firebase.js'

// Firebase v11+ Auth error codes mapping for consistent UX
const AUTH_ERROR_MESSAGES = Object.freeze({
  'auth/wrong-password': 'The password you entered is incorrect.',
  'auth/invalid-credential': 'The credentials provided are invalid.',
  'auth/email-already-in-use': 'This email address is already in use by another account.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/requires-recent-login': 'For security, please sign out and sign back in before continuing.',
  'auth/weak-password': 'The password is too weak. Please choose a stronger password.',
  'auth/user-mismatch': 'The credentials do not match the current user.',
  'auth/user-not-found': 'No user account found with this email.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/operation-not-allowed': 'This operation is not currently enabled.',
  'auth/admin-restricted-operation': 'This operation is restricted by the administrator.',
  'auth/credential-already-in-use': 'This credential is already associated with a different user account.',
  'auth/provider-already-linked': 'This email is already linked to your account through another sign-in method.',
  'auth/popup-closed-by-user': 'The sign-in popup was closed before completing authentication.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled.',
  'auth/network-request-failed': 'Network error occurred. Please check your connection.',
})

const PROVIDER_ERROR_MESSAGE = 'This operation is only supported for users who signed up with email and password. If you signed in with Google or another provider, please contact support.'

/**
 * Firebase v11+ Authentication Hook
 * 
 * Provides comprehensive authentication state management using Firebase Auth v11+
 * with optimized re-renders, modern async patterns, and enhanced error handling.
 * 
 * Features:
 * - React 19+ optimized with useCallback for minimal re-renders
 * - Firebase v11+ modular SDK patterns
 * - Comprehensive error handling with user-friendly messages
 * - Parallel operations for better performance
 * - TypeScript-ready with JSDoc annotations
 * 
 * @returns {Object} Authentication state and methods
 * @returns {Object|null} user - Current Firebase user object or null
 * @returns {boolean} loading - Loading state during initial auth check
 * @returns {Function} login - Email/password login (email, password) => Promise<UserCredential>
 * @returns {Function} register - User registration (email, password, displayName?) => Promise<UserCredential>
 * @returns {Function} loginWithGoogle - Google OAuth login () => Promise<UserCredential>
 * @returns {Function} resetPassword - Send password reset email (email) => Promise<{success, message}>
 * @returns {Function} changeEmail - Change user email (newEmail, currentPassword) => Promise<{success, message, pendingEmail}>
 * @returns {Function} changePassword - Change user password (currentPassword, newPassword) => Promise<{success, message}>
 * @returns {Function} deleteAccount - Delete user account (currentPassword) => Promise<{success, message}>
 * @returns {Function} sendVerificationEmail - Send email verification () => Promise<{success, message}>
 * @returns {Function} resendVerificationEmail - Alias for sendVerificationEmail
 * @returns {Function} refreshUser - Refresh user state () => Promise<User|null>
 * @returns {Function} updateUserProfile - Update user profile (profileData) => Promise<{success, message, user}>
 * @returns {Function} logout - Sign out user () => Promise<{success, message}>
 */
export function useAuth() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // Firebase v11+ optimized auth state management
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, 
      user => {
        setUser(user)
        setLoading(false)
      },
      () => {
        setUser(null)
        setLoading(false)
      },
    )
    
    return unsubscribe
  }, [])

  // Utility function for handling Firebase Auth errors
  const handleAuthError = useCallback((error, operation) => {
    const message = AUTH_ERROR_MESSAGES[error.code] ?? 
      `Failed to ${operation.toLowerCase()}: ${error.message ?? 'Unknown error occurred'}`
    
    throw new Error(message)
  }, [])

  // Utility function to check if user has email/password provider
  const requiresPasswordAuth = useCallback((currentUser = auth.currentUser) => {
    if (!currentUser?.email) {
      throw new Error(currentUser ? 'Current user has no email address' : 'No authenticated user found')
    }
    
    const hasEmailProvider = currentUser.providerData?.some(p => p.providerId === 'password')
    if (!hasEmailProvider) {
      throw new Error(PROVIDER_ERROR_MESSAGE)
    }
    
    return currentUser
  }, [])

  // Authentication methods with Firebase v11+ optimized patterns
  const login = useCallback(async (email, password) => {
    try {
      return await signInWithEmailAndPassword(auth, email, password)
    } catch (error) {
      handleAuthError(error, 'Login')
    }
  }, [handleAuthError])
  
  const register = useCallback(async (email, password, displayName) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      
      // Firebase v11+ optimized parallel operations
      const tasks = []
      
      // Profile update task
      if (displayName) {
        tasks.push(updateProfile(userCredential.user, { displayName }))
      }
      
      // Firestore document creation with server timestamp
      tasks.push(
        setDoc(doc(db, 'users', userCredential.user.uid), {
          email: userCredential.user.email,
          displayName: displayName ?? '',
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
        }),
      )
      
      // Email verification task
      tasks.push(sendEmailVerification(userCredential.user))
      
      await Promise.all(tasks)
      return userCredential
    } catch (error) {
      handleAuthError(error, 'Registration')
    }
  }, [handleAuthError])

  const loginWithGoogle = useCallback(async () => {
    try {
      const provider = new GoogleAuthProvider()
      // Firebase v11+ scopes configuration
      provider.addScope('https://www.googleapis.com/auth/userinfo.email')
      provider.addScope('https://www.googleapis.com/auth/userinfo.profile')
      
      // Custom parameters for better UX
      provider.setCustomParameters({
        prompt: 'select_account',
      })
      
      return await signInWithPopup(auth, provider)
    } catch (error) {
      handleAuthError(error, 'Google Sign-In')
    }
  }, [handleAuthError])

  const resetPassword = useCallback(async (email) => {
    try {
      await sendPasswordResetEmail(auth, email, {
        url: `${globalThis.location.origin}/login`,
        handleCodeInApp: false,
      })
      return { success: true, message: 'Password reset email sent successfully.' }
    } catch (error) {
      handleAuthError(error, 'Password Reset')
    }
  }, [handleAuthError])

  const sendVerificationEmail = useCallback(async () => {
    try {
      const { currentUser } = auth
      if (!currentUser) {
        throw new Error('No user is currently signed in')
      }
      
      if (currentUser.emailVerified) {
        return { success: true, message: 'Email is already verified.' }
      }
      
      await sendEmailVerification(currentUser, {
        url: `${globalThis.location.origin}/profile`,
        handleCodeInApp: false,
      })
      
      return { success: true, message: 'Verification email sent successfully.' }
    } catch (error) {
      handleAuthError(error, 'Send Verification Email')
    }
  }, [handleAuthError])

  const refreshUser = useCallback(async () => {
    try {
      const { currentUser } = auth
      if (!currentUser) return null
      
      await currentUser.reload()
      
      // Get fresh user data and update state
      const refreshedUser = auth.currentUser
      setUser(refreshedUser)
      
      return refreshedUser
    } catch {
      return null
    }
  }, [])

  // Streamlined email change with proper error handling
  const changeEmail = useCallback(async (newEmail, currentPassword) => {
    try {
      const user = requiresPasswordAuth()
      
      // Re-authenticate and send verification email
      const credential = EmailAuthProvider.credential(user.email, currentPassword)
      await reauthenticateWithCredential(user, credential)
      
      await verifyBeforeUpdateEmail(user, newEmail, {
        url: `${globalThis.location.origin}/profile`,
        handleCodeInApp: false,
      })
      
      return {
        success: true,
        message: `A verification email has been sent to ${newEmail}. Please check your email and click the verification link to complete the email change.`,
        pendingEmail: newEmail,
      }
    } catch (error) {
      handleAuthError(error, 'Email change')
    }
  }, [requiresPasswordAuth, handleAuthError])

  // Streamlined password change with proper error handling
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const user = requiresPasswordAuth()
      
      // Re-authenticate and update password
      const credential = EmailAuthProvider.credential(user.email, currentPassword)
      await reauthenticateWithCredential(user, credential)
      await updatePassword(user, newPassword)
      
      return {
        success: true,
        message: 'Password updated successfully!',
      }
    } catch (error) {
      handleAuthError(error, 'Password change')
    }
  }, [requiresPasswordAuth, handleAuthError])

  // Streamlined account deletion with proper error handling
  const deleteAccount = useCallback(async (currentPassword) => {
    try {
      const user = requiresPasswordAuth()
      
      // Re-authenticate and delete account
      const credential = EmailAuthProvider.credential(user.email, currentPassword)
      await reauthenticateWithCredential(user, credential)
      await deleteUser(user)
      
      return {
        success: true,
        message: 'Account deleted successfully.',
      }
    } catch (error) {
      handleAuthError(error, 'Account deletion')
    }
  }, [requiresPasswordAuth, handleAuthError])

  const logout = useCallback(async () => {
    try {
      await signOut(auth)
      return { success: true, message: 'Logged out successfully.' }
    } catch (error) {
      handleAuthError(error, 'Logout')
    }
  }, [handleAuthError])

  // Firebase v11+ optimized profile update with enhanced error handling
  const updateUserProfile = useCallback(async (profileData) => {
    try {
      const { currentUser } = auth
      if (!currentUser) {
        throw new Error('No user is currently signed in')
      }

      const tasks = []
      
      // Firebase Auth profile update
      if (profileData.displayName !== undefined) {
        tasks.push(updateProfile(currentUser, { displayName: profileData.displayName }))
      }

      // Firestore user document update with server timestamp
      const firestoreUpdate = {
        updatedAt: serverTimestamp(),
        ...(profileData.displayName !== undefined && { displayName: profileData.displayName }),
      }
      
      tasks.push(
        setDoc(doc(db, 'users', currentUser.uid), firestoreUpdate, { merge: true }),
      )

      await Promise.all(tasks)
      
      // Refresh user state with updated data
      const updatedUser = await refreshUser()
      
      return { 
        success: true, 
        message: 'Profile updated successfully.',
        user: updatedUser,
      }
    } catch (error) {
      handleAuthError(error, 'Profile Update')
    }
  }, [refreshUser, handleAuthError])

  return { 
    user, 
    loading, 
    login, 
    register, 
    loginWithGoogle,
    resetPassword,
    changeEmail,
    sendVerificationEmail,
    resendVerificationEmail: sendVerificationEmail, // Alias for backward compatibility
    refreshUser,
    updateUserProfile,
    logout,
    changePassword,
    deleteAccount,
  }
}
